<template>
    <span ref="select" v-click-outside="hide"
          :class="{
                'select2 select2-container select2-container--default select2-container--above': true,
                'select2-container--disabled': disabled,
                'select2-container--focus select2-container--open': showed
          }" @click="toggleSelect">
        <span class="selection">
            <span class="select2-selection select2-selection--single" >
                <span class="select2-selection__rendered">
                    <span v-if="canBeEmpty && modelValue.length" class="select2-selection__clear" @click.stop="$emit('update:modelValue', [])">×</span>
                    <slot name="optionContent" v-if="modelValue.length">{{ selectedValue }}</slot>
                    <span name="placeholder" v-else class="select2-selection__placeholder">{{ placeholder }}</span>
                </span>
                    <span class="select2-selection__arrow" role="presentation">
                    <b role="presentation"></b>
                </span>
            </span>
        </span>
        <span class="dropdown-wrapper" aria-hidden="true"></span>
    </span>

    <Teleport to="body" v-if="showed">
        <span
            ref="options"
            @click.stop
            :class="{
                'select2-container select2-container--default': true,
                'select2-container--open': showed,
            }"
            :style="{ 'z-index': zIndex, position: 'absolute' }"
        >
        <span class="select2-dropdown select2-dropdown--above">
            <span class="select2-search select2-search--dropdown">
                <input class="select2-search__field" ref="searchInput" v-model="search" @input="filterOptions" :placeholder="translate('alerts.enter_some_characters')">
            </span>
            <span class="select2-results">
                <ul class="select2-results__options" aria-expanded="true" aria-hidden="false" ref="list">
                    <slot name="additionalOptions" :selectValue="selectValue"></slot>
                    <li
                        :class="{
                            'select2-results__option': true,
                            'select2-results__option--highlighted': index === highlightedIndex
                        }"
                        :aria-selected="optionIsSelected(option)"
                        v-for="(option, index) in filteredOptions"
                        :title="findByPath(option, optionValue)"
                        :aria-disabled="disabledFields.includes(option[optionKey])"
                        @mouseover="highlightedIndex = index"
                        @click.stop="disabledFields.includes(option[optionKey]) ? null : selectValue(option)"
                    >
                        {{ findByPath(option, optionValue) }}
                    </li>
                    <li v-if="filteredOptions.length === 0" class="select2-results__option select2-results__message">{{translate('alerts.no_records_found')}}</li>
                </ul>
            </span>
            <div v-if="hasCreateButton" class="options-footer">
                <AlphaBackgroundButton color="teal" @click="$emit('click:create')">
                    {{ translate('buttons.create') }}
                </AlphaBackgroundButton>
            </div>
        </span>
        </span>
    </Teleport>
</template>
<script>
import { toRaw } from 'vue'
import vClickOutside from 'click-outside-vue3'
import { createPopper } from '@popperjs/core';
import AlphaBackgroundButton from "../../button/AlphaBackgroundButton.vue";

export default {
    name: "AppMultiselect",
    components: {
        AlphaBackgroundButton
    },
    props: {
        modelValue: {
            type: Array,
            default: () => []
        },
        options: {
            type: Array,
            required: true,
            default: () => []
        },
        optionKey: {
            type: String,
            default: 'uuid'
        },
        optionValue: {
            type: String,
            default: 'name'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: () => translate('components.select.default_placeholder')
        },
        hasCreateButton: {
            type: Boolean,
            default: false
        },
        canBeEmpty: {
            type: Boolean,
            default: true
        },
        disabledFields: {
            type: Array,
            default: () => []
        }
    },
    emits: ['update:modelValue', 'click:create'],
    data() {
        return {
            search: '',
            showed: false,
            highlightedIndex: null,
            zIndex: 950,
            debounceTimer: null,
            popperInstance: null,
        }
    },
    methods: {
        optionIsSelected(option) {
            const selectedOptionKeys = this.modelValue.map(option => 
                this.findByPath(option, this.optionKey)
            );

            const optionKey = this.findByPath(option, this.optionKey);

            return selectedOptionKeys.includes(optionKey);
        },
        toggleSelect() {
            if (this.disabled) {
                return;
            }

            this.showed ? this.hide() : this.show();
        },
        show() {
            if (this.showed) {
                return;
            }

            this.highlightedIndex = this.options.length > 0 ? 0 : null;
            this.showed = true;

            this.$nextTick(() => {
                if (!this.$refs.select) {
                    this.showed = false;
                    return;
                }

                if (this.$refs?.searchInput) {
                    this.$refs.searchInput.focus();
                }

                addEventListener('keydown', this.handleKeyDown);

                this.createPopper();
            });
        },
        hide() {
            if (!this.showed) {
                return;
            }

            this.showed = false;
            this.destroyPopper();
            this.search = '';
            this.highlightedIndex = null;
            removeEventListener('keydown', this.handleKeyDown);
        },
        createPopper() {
            if (this.$refs.select && this.$refs.options) {
                this.popperInstance = createPopper(this.$refs.select, this.$refs.options, {
                    placement: 'bottom-start',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 2],
                            },
                        },
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top-start'],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'clippingParents',
                            },
                        },
                        {
                            name: 'sameWidth',
                            enabled: true,
                            phase: 'beforeWrite',
                            requires: ['computeStyles'],
                            fn: ({ state }) => {
                                state.styles.popper.width = `${state.rects.reference.width}px`;
                            },
                            effect: ({ state }) => {
                                state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
                            },
                        },
                    ],
                });
            }
        },
        destroyPopper() {
            if (this.popperInstance) {
                this.popperInstance.destroy();
                this.popperInstance = null;
            }
        },
        handleKeyDown(event) {
            if (event.key === 'ArrowUp') {
                this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
                event.preventDefault();
                this.scrollIntoViewIfNeeded();
            } else if (event.key === 'ArrowDown') {
                this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1);
                event.preventDefault();
                this.scrollIntoViewIfNeeded();
            } else if (event.key === 'Enter') {
                if (this.filteredOptions[this.highlightedIndex] === undefined) {
                    return;
                };
                this.selectValue(this.filteredOptions[this.highlightedIndex])
            }
        },
        scrollIntoViewIfNeeded() {
            const option = this.$refs.list.children[this.highlightedIndex];
            if (option) {
                option.scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest',
                    behavior: 'smooth'
                });
            }
        },
        findByPath(object, patch) {
            let parts = patch.split('.');

            if (parts.length === 1) {
                return object[parts[0]];
            }

            return this.findByPath(object[parts[0]], parts.slice(1).join('.'));
        },
        filterOptions() {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.$nextTick(() => {
                    if (this.filteredOptions.length > 0) {
                        this.highlightedIndex = 0;
                    }
                });
            }, 300);
        },
        selectValue(newValue) {
            const isSelected = this.modelValue.some(item => 
                this.findByPath(item, this.optionKey) === this.findByPath(newValue, this.optionKey)
            );

            let newArray;
            if (isSelected) {
                newArray = this.modelValue
                    .filter(item => 
                        this.findByPath(item, this.optionKey) !== this.findByPath(newValue, this.optionKey)
                    )
            } else {
                newArray = [...this.modelValue, newValue];
            } 
            
            newArray = newArray.map(item => toRaw(item))

            this.$emit('update:modelValue', newArray);
        }
    },
    computed: {
        selectedValue() {
            return this.modelValue.map((value) => this.findByPath(value, this.optionValue)).join(', ')
        },
        filteredOptions() {

            const searchLower = this.search.toLowerCase();
            return this.options.filter(option => {
                const optionText = this.findByPath(option, this.optionValue);
                return optionText && optionText.toLowerCase().includes(searchLower);
            });
        }
    },
    mounted() {
        let modalCount = document.getElementsByClassName('modal-backdrop').length

        if (modalCount > 0) {
            this.zIndex = 1000 + (modalCount * 10) + 7
        }
    },
    unmounted() {
        removeEventListener('keydown', this.handleKeyDown);
        this.destroyPopper();
    },
    watch: {
        filteredOptions() {
            setTimeout(() => {
                if (this.popperInstance) {
                    this.popperInstance.update();
                }
                if (this.filteredOptions.length > 0) {
                    this.highlightedIndex = 0
                }
            }, 0)
        }
    },
    directives: {
        clickOutside: vClickOutside.directive
    }
}
</script>
<style scoped>
.options-footer {
    border-top: 1px solid #ddd;
    padding: .5rem;
}
.select2 {
    width: 100%;
}
.select2-results__option {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>